/**
 * Utility functions to parse and extract data from PersonalizationRule rules and actions JSON
 */

/**
 * Extracts rule input types (triggers) from the rules JSON string
 * @param rulesJson - JSON string containing rule logic
 * @param builderConfig - Optional builder configuration for field labels
 * @returns Array of input type names
 */
export const extractRuleInputs = (rulesJson: string, builderConfig?: any): string[] => {
    try {
        if (!rulesJson || rulesJson === '{}' || rulesJson === 'true') {
            return []
        }

        const rules = JSON.parse(rulesJson)
        const inputs = new Set<string>()

        // Parse builder config to get field labels if available
        let fieldLabels: Record<string, string> = {}
        if (builderConfig?.personalizationRuleBuilderConfig) {
            try {
                const config = JSON.parse(builderConfig.personalizationRuleBuilderConfig)
                if (config) {
                    // Recursively extract field labels, including nested subfields
                    const extractLabels = (fields: any, parentPath: string = ''): void => {
                        Object.keys(fields).forEach((fieldKey) => {
                            const field = fields[fieldKey]
                            const currentPath = parentPath ? `${parentPath}.${fieldKey}` : fieldKey

                            if (field && field.label) {
                                fieldLabels[currentPath] = field.label
                            }

                            // Handle nested subfields
                            if (field && field.subfields) {
                                extractLabels(field.subfields, currentPath)
                            }
                        })
                    }

                    extractLabels(config)
                }
            } catch (configError) {
                console.warn('Could not parse builder config:', configError)
            }
        }

        // Recursive function to traverse the JSON logic structure
        const traverseRules = (obj: any): void => {
            if (typeof obj !== 'object' || obj === null) {
                return
            }

            // Handle arrays
            if (Array.isArray(obj)) {
                obj.forEach(traverseRules)
                return
            }

            // Handle objects
            Object.entries(obj).forEach(([key, value]) => {
                // Look for variable references in JSON logic format
                if (key === 'var' && typeof value === 'string') {
                    // First try to get label from builder config
                    let displayName = fieldLabels[value]

                    if (!displayName) {
                        // Fallback to manual mapping for common cases
                        const inputTypeMap: Record<string, string> = {
                            website: 'Website',
                            retailer: 'Retailer',
                            retailer_id: 'Retailer',
                            date: 'Date',
                            market_group: 'Market Group',
                            tag: 'Tag',
                            user: 'User',
                            customer: 'Customer',
                            product: 'Product',
                            category: 'Category',
                            price: 'Price',
                            location: 'Location',
                            time: 'Time',
                            device: 'Device',
                            browser: 'Browser',
                            referrer: 'Referrer',
                            campaign: 'Campaign',
                            segment: 'Segment'
                        }

                        displayName =
                            inputTypeMap[value] || value.charAt(0).toUpperCase() + value.slice(1)
                    }

                    inputs.add(displayName)
                }

                // Recursively traverse nested objects
                traverseRules(value)
            })
        }

        traverseRules(rules)
        return Array.from(inputs).sort()
    } catch (error) {
        console.error('Error parsing rules JSON:', error)
        return []
    }
}

/**
 * Extracts action types from the actions JSON string
 * @param actionsJson - JSON string containing action configuration
 * @returns Array of action type names
 */
export const extractActionTypes = (actionsJson: string): string[] => {
    try {
        if (!actionsJson || actionsJson === '{}') {
            return []
        }

        const actions = JSON.parse(actionsJson)
        const actionTypes = new Set<string>()

        // The actions structure typically has an "actions" array
        if (actions.actions && Array.isArray(actions.actions)) {
            actions.actions.forEach((action: any) => {
                if (action && typeof action === 'object') {
                    // Look for action type indicators, but exclude structural keys
                    Object.keys(action).forEach((key) => {
                        // Skip structural/metadata keys that aren't action types
                        const structuralKeys = [
                            'action',
                            'Action',
                            'id',
                            'type',
                            'Type',
                            'name',
                            'Name',
                            'label',
                            'Label',
                            'enabled',
                            'active',
                            'priority',
                            'order',
                            'metadata',
                            'timestamp',
                            'created_at',
                            'updated_at'
                        ]
                        if (structuralKeys.includes(key)) {
                            return
                        }

                        // Map action keys to user-friendly display names
                        const actionTypeMap: Record<string, string> = {
                            configurator_personalization: 'Configurator Personalization',
                            discount: 'Discount',
                            preset_availability: 'Preset Availability',
                            variable_replace: 'Variable Replace',
                            content_personalization: 'Content Personalization',
                            product_recommendation: 'Product Recommendation',
                            price_adjustment: 'Price Adjustment',
                            visibility_control: 'Visibility Control',
                            redirect: 'Redirect',
                            banner: 'Banner',
                            popup: 'Popup',
                            email_trigger: 'Email Trigger',
                            notification: 'Notification'
                        }

                        if (action[key] !== undefined && action[key] !== null) {
                            const displayName =
                                actionTypeMap[key] ||
                                key
                                    .split('_')
                                    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                                    .join(' ')
                            actionTypes.add(displayName)
                        }
                    })
                }
            })
        }

        return Array.from(actionTypes).sort()
    } catch (error) {
        console.error('Error parsing actions JSON:', error)
        return []
    }
}

/**
 * Formats an array of strings into a comma-separated display string
 * @param items - Array of items to format
 * @param fallback - Fallback text when array is empty (default: "—")
 * @returns Formatted string
 */
export const formatDisplayList = (items: string[], fallback: string = '—'): string => {
    return items.length > 0 ? items.join(', ') : fallback
}
